import React, { useEffect, useRef, useState } from 'react';
import { useIsInViewport } from '../../../../shared/hooks/useIsInViewport';
import { useSearchQuery } from '../../../../shared/hooks/useSearchQuery';
import { MainChat } from './components/chat/MainChat';
import { SideChat } from './components/chat/SideChat';
import { MessageFilters } from './components/filters/MessageFilters';
import { Col, Row } from 'react-bootstrap';
import { MessageService } from './service';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { ErrorToast } from '../../../../utils/toasters';
import { IParams } from '../../../../model/shared/IParams';
import {
    IMessageContent,
    IMessageOpenedChat,
    IMessageQuery,
    IMessageThread,
    IMessageThreadQuery,
} from './dto/IMessage';
import { useTranslation } from 'react-i18next';

let messageInterval: any;

export const CampaignMessages = () => {
    const { t } = useTranslation();
    const { state } = useLocation();
    const navigate = useNavigate();
    const { id } = useParams<keyof IParams>();
    const threadId = Number(useSearchQuery('threadId'));

    const [isMobile, setIsMobile] = useState(true);
    const [filterParams, setFilterParams] = useState<IMessageQuery>({
        page: 1,
        perPage: 15,
        campaignId: id,
    });
    const [count, setCount] = useState(0);
    const [threadCount, setThreadCount] = useState({
        count: 0,
        nbUnreadThreads: 0,
    });
    const [selectedThread, setSelectedThread] = useState<IMessageThread>();
    const [triggerUpdate, setTriggerUpdate] = useState(false);
    const [inboxMessages, setInboxMessages] = useState<IMessageContent[]>();
    const [selectedChatId, setSelectedChatId] = useState<number>();
    const [selectedChatContent, setSelectedChatContent] = useState<IMessageOpenedChat[]>([]);
    const [chatParams, setChatParams] = useState<IMessageThreadQuery>({
        page: 1,
        perPage: 15,
    });

    useEffect(() => {
        setIsMobile(window.innerWidth <= 550);
    }, []);

    const shouldShowSideChat = (isMobileParams: boolean) => {
        return isMobileParams ? !selectedChatId && isMobile : true;
    };

    const shouldShowMainChat = (isMobileParams: boolean) => {
        return isMobileParams ? selectedChatId && isMobile : true;
    };

    useEffect(() => {
        getInitialMessages(selectedChatId);
    }, [chatParams?.page]);

    useEffect(() => {
        getInitialMessages(selectedChatId, true);
    }, [selectedChatId]);

    useEffect(() => {
        id &&
            MessageService.getInboxMessages({ ...filterParams, campaignId: +id })
                .then(response => {
                    const { data, count, nbUnreadThreads } = response;
                    if (data) {
                        setThreadCount({
                            count,
                            nbUnreadThreads,
                        });
                        setInboxMessages(data);

                        if (state?.fromMessageScreen) {
                            setSelectedChatId(state?.threadId ? state?.threadId : data[0]?.id);
                            // navigate({...history.location, state: undefined});
                            navigate('some-route', { state: undefined });
                        } else if (!selectedChatId && !isMobile) {
                            setSelectedChatId(!!threadId ? threadId : state?.threadId ? state?.threadId : data[0]?.id);
                        }
                    }
                })
                .catch(error => ErrorToast(error));
    }, [id, triggerUpdate, JSON.stringify(filterParams), isMobile, selectedChatId]);

    useEffect(() => {
        clearInterval(messageInterval);
        if (selectedChatId) {
            messageInterval = setInterval(() => {
                getMessagesPolling(selectedChatId);
            }, 5000);
        }

        return () => {
            clearInterval(messageInterval);
        };
    }, [selectedChatId, triggerUpdate, JSON.stringify(selectedChatContent)]);

    function getInitialMessages(selectedChatId?: number, isInitial?: boolean) {
        selectedChatId &&
            MessageService.getInboxMessageById({
                ...chatParams,
                id: selectedChatId,
            })
                .then(response => {
                    setSelectedThread(response?.thread);
                    setCount(response?.count);
                    const concated = isInitial ? response?.data : selectedChatContent?.concat(response.data);
                    setSelectedChatContent(concated);
                })
                .catch(error => ErrorToast(error));
    }

    function getMessagesPolling(selectedChatId?: number) {
        !!selectedChatContent?.length &&
            MessageService.getInboxMessageById({
                page: 1,
                perPage: 15,
                id: selectedChatId,
            })
                .then(response => {
                    const hasNewMessage = (selectedChatContent?.[0] as any)?.id !== response?.data[0]?.id;
                    if (hasNewMessage) {
                        setTriggerUpdate(!triggerUpdate);
                        const myLastMessageIndex = response?.data?.findIndex((message: { id: number }) => {
                            return message.id === (selectedChatContent?.[0] as any)?.id;
                        });
                        setSelectedChatContent(prev => [...response?.data.slice(0, myLastMessageIndex), ...prev]);
                    }
                })
                .catch(error => ErrorToast(error));
    }

    const lastSideChatRef = useRef<HTMLDivElement>(null);
    const isInViewport1 = useIsInViewport(lastSideChatRef);

    useEffect(() => {
        // don't send api request if array is empty and don't send it if we have all messages
        if (isInViewport1 && !!inboxMessages?.length && filterParams?.perPage <= threadCount?.count) {
            setFilterParams(prev => ({ ...prev, perPage: prev.perPage + 10 }));
        }
    }, [isInViewport1, threadCount?.count]);

    return (
        <Row className="w-100 mx-0 border default-radius">
            {shouldShowSideChat(isMobile) && (
                <Col md={3} className="px-0">
                    <MessageFilters filterParams={filterParams} count={threadCount} setFilterParams={setFilterParams} />
                    <div className="bg-white responsive-chat-body-height">
                        {!!inboxMessages?.length ? (
                            inboxMessages?.map(message => (
                                <SideChat
                                    key={message?.id}
                                    isDeletedFilter={filterParams?.isDeleted as boolean}
                                    setTriggerUpdate={setTriggerUpdate}
                                    clickHandler={() => {
                                        setChatParams(prev => ({ ...prev, page: 1 }));
                                        setSelectedChatId(message?.id);
                                    }}
                                    selectedChatId={selectedChatId as number}
                                    message={message}
                                />
                            ))
                        ) : (
                            <p className="p-3">{t('influencer.messages.noConversation')}</p>
                        )}
                        <div ref={lastSideChatRef} className="py-1" />
                    </div>
                </Col>
            )}
            <Col md={9} className="h-100 px-0">
                {shouldShowMainChat(isMobile) && (
                    <MainChat
                        selectedChatContent={selectedChatContent}
                        selectedChatId={selectedChatId as number}
                        setSelectedChatId={setSelectedChatId}
                        getMessagesPolling={getMessagesPolling}
                        selectedThread={selectedThread!}
                        count={count}
                        setChatParams={setChatParams}
                        chatParams={chatParams}
                    />
                )}
            </Col>
        </Row>
    );
};
